import { useEffect, useRef, useState } from "react";
import { useAchievements } from "../../hooks/useAchievements";
import { useUserXp } from "../../hooks/useUserXp";
import * as analytics from "../../utils/analytics";
import { AchievementCelebration, achievementCelebration, featureDiscoveryCelebration, levelCelebration } from "../../utils/achievementCelebration";
import { discoveriesCrossedByLevel } from "../../utils/featureDiscovery";
import AchievementCelebrationDialog from "./AchievementCelebrationDialog";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { subscribeMilestones } from "../../utils/milestoneEvents";

// Mounted once in the header. It serializes level, discovery and achievement
// milestones so only one modal can hold focus at a time.
function AchievementUnlockedNotice() {
    const { newlyUnlocked, isReady, seen = {}, markSeen } = useAchievements();
    const { progress, hasProgress } = useUserXp();
    const [queue, setQueue] = useState<Array<{ achievement: AchievementCelebration; continueTo: string }>>([]);
    const announcedRef = useRef(new Set<string>());
    const previousLevelRef = useRef<number>();
    const lp = useLocalizedPath();

    useEffect(() => subscribeMilestones((event) => {
        setQueue((current) => [...current, event]);
    }), []);

    useEffect(() => {
        if (!hasProgress || !isReady) return;
        const previous = previousLevelRef.current;
        previousLevelRef.current = progress.level;
        if (previous === undefined || progress.level <= previous) return;
        const milestones = [
            { achievement: levelCelebration(progress.level), continueTo: lp("/profile") },
            ...discoveriesCrossedByLevel(previous, progress.level).map((discovery) => ({
                achievement: featureDiscoveryCelebration(discovery),
                continueTo: lp(discovery.path),
            })),
        ].filter(({ achievement }) => !announcedRef.current.has(achievement.id) && seen[achievement.id] !== "seen");
        if (!milestones.length) return;
        milestones.forEach(({ achievement }) => {
            announcedRef.current.add(achievement.id);
            markSeen(achievement.id);
            analytics.track("achievement_celebration_displayed", { achievement_type: achievement.kind });
        });
        setQueue((current) => [...current, ...milestones]);
    }, [hasProgress, isReady, progress.level, seen, markSeen, lp]);

    useEffect(() => {
        if (!isReady || newlyUnlocked.length === 0) return;
        const fresh = newlyUnlocked.filter((achievement) => !announcedRef.current.has(achievement.id));
        if (!fresh.length) return;
        fresh.forEach((achievement) => {
            announcedRef.current.add(achievement.id);
            markSeen(achievement.id);
            analytics.track("achievement_unlocked", { achievement_id: achievement.id });
            analytics.track("achievement_celebration_displayed", { achievement_type: "achievement" });
        });
        setQueue((current) => [...current, ...fresh.map((achievement) => ({ achievement: achievementCelebration(achievement), continueTo: lp("/profile") }))]);
    }, [isReady, newlyUnlocked, markSeen, lp]);

    const current = queue[0];
    if (!current) return null;

    return (
        <AchievementCelebrationDialog achievement={current.achievement} continueTo={current.continueTo} onDismiss={() => setQueue((items) => items.slice(1))} />
    );
}

export default AchievementUnlockedNotice;
